import * as React from 'react';
import styled, { css } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { useIsOverflowing } from '../../internals/useIsOverflowing';
import { Tooltip } from '../../composites/Tooltip';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';
import { mediaQuery } from '../../tokens/breakpoints';

/* ─────────────────────────── Responsive Columns ──────────────────────── */

export type ResponsiveColumns = {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

export type ColumnsSpec = number | ResponsiveColumns;

function resolveResponsiveColumns(spec: ColumnsSpec): ResponsiveColumns {
  if (typeof spec === 'number') {
    if (spec <= 1) return { base: 1 };
    if (spec === 2) return { base: 1, sm: 2 };
    return { base: 1, sm: 2, lg: spec };
  }
  return spec;
}

function buildGridCss(resolved: ResponsiveColumns) {
  const base = resolved.base ?? 1;
  const entries = Object.entries(resolved).filter(([k]) => k !== 'base') as Array<
    [keyof typeof mediaQuery, number]
  >;

  return css`
    grid-template-columns: repeat(${base}, minmax(0, 1fr));
    ${entries.map(
      ([bp, cols]) => css`
        ${mediaQuery[bp]} {
          grid-template-columns: repeat(${cols}, minmax(0, 1fr));
        }
      `,
    )}
  `;
}

/* ─────────────────────────── DescriptionList.Root ─────────────────────── */

export interface DescriptionListRootProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns or responsive breakpoint spec. Auto-collapses on mobile when a number is passed. */
  columns?: ColumnsSpec;
  semanticId?: string;
}

const shouldForwardRoot = (prop: string | number): boolean =>
  !['$responsive'].includes(String(prop));

const StyledRoot = styled.div.withConfig({ shouldForwardProp: shouldForwardRoot })<{
  $responsive: ResponsiveColumns;
}>`
  display: grid;
  ${({ $responsive }) => buildGridCss($responsive)}
  gap: 0;
  width: 100%;
`;

const DescriptionListRoot = React.forwardRef<HTMLDivElement, DescriptionListRootProps>(
  function DescriptionListRoot({ columns = 1, semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    const resolved = resolveResponsiveColumns(columns);
    return <StyledRoot ref={ref} $responsive={resolved} {...semanticProps} {...rest} />;
  },
);

DescriptionListRoot.displayName = 'DescriptionList';

/* ─────────────────────────── DescriptionList.Item ─────────────────────── */

export type DescriptionListItemLayout = 'stacked' | 'inline' | 'detail' | 'cell';
export type DescriptionListItemSize = 'sm' | 'md';

export interface DescriptionListItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value?: React.ReactNode;
  placeholder?: string;
  action?: React.ReactNode;
  /** 'stacked' = label above value (default). 'inline' = icon+label on left, value on right. 'detail' = icon-left flex row with label above value (entity detail views). */
  layout?: DescriptionListItemLayout;
  /** Text size: 'sm' = 14px (default), 'md' = 16px. Applies to inline and stacked layouts. */
  size?: DescriptionListItemSize;
  /** When true, value text is truncated with ellipsis and a tooltip shows the full text on hover when overflowing. */
  truncate?: boolean;
  semanticId?: string;
}

const TRANSIENT_ITEM = ['$layout'] as const;
const shouldForwardItem = (prop: string | number): boolean =>
  !TRANSIENT_ITEM.includes(prop as (typeof TRANSIENT_ITEM)[number]);

const StyledItem = styled.div.withConfig({ shouldForwardProp: shouldForwardItem })<{
  $layout: DescriptionListItemLayout;
}>`
  display: grid;
  align-items: flex-start;
  padding: ${rem(16)} 0;
  border-bottom: 1px solid var(--ui-border-muted, ${palette.CulturedWhite});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);

  ${({ $layout }) =>
    $layout === 'inline'
      ? css`
          grid-template-columns: 1fr 2fr;
          gap: ${rem(50)};

          @media (max-width: 640px) {
            grid-template-columns: 1fr;
            gap: ${rem(8)};
          }
        `
      : css`
          grid-template-columns: 1fr;
          gap: ${rem(4)};
        `}

  &:last-child {
    border-bottom: none;
  }
`;

const StyledIconSlot = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(5)};
  color: var(--ui-text-muted, ${palette.SpanishGray});

  .material-symbols-outlined {
    font-size: ${rem(20)};
  }
`;

const StyledContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
`;

const StyledLabel = styled.span<{ $size?: DescriptionListItemSize }>`
  font-size: ${({ $size }) => ($size === 'md' ? rem(16) : rem(14))};
  line-height: ${({ $size }) => ($size === 'md' ? rem(22) : rem(18))};
  color: var(--ui-text, ${palette.Black});
`;

const StyledStackedLabel = styled.span`
  font-size: ${rem(12)};
  font-weight: 500;
  color: var(--ui-text-muted, ${palette.Gray});
  letter-spacing: 0.02em;
`;

const StyledValue = styled.span<{ $size?: DescriptionListItemSize }>`
  font-size: ${({ $size }) => ($size === 'md' ? rem(16) : rem(14))};
  line-height: ${({ $size }) => ($size === 'md' ? rem(22) : rem(18))};
  color: var(--ui-text, ${palette.Black});
  word-break: break-word;
`;

const StyledPlaceholder = styled.span<{ $size?: DescriptionListItemSize }>`
  font-size: ${({ $size }) => ($size === 'md' ? rem(16) : rem(14))};
  line-height: ${({ $size }) => ($size === 'md' ? rem(22) : rem(18))};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
`;

const StyledAction = styled.div`
  flex-shrink: 0;
  margin-left: auto;
`;

/* ── Detail mode styled components (legacy SingleDetailBlock pattern) ── */

const StyledDetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding-bottom: ${rem(18)};
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
`;

const StyledDetailIconSlot = styled.span`
  font-size: ${rem(16)};
  color: var(--ui-text-muted, ${palette.SpanishGray});
  margin-right: ${rem(10)};
  display: flex;
  align-items: center;
  flex-shrink: 0;

  .material-symbols-outlined,
  .material-symbols-sharp {
    font-size: ${rem(16)};
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 20;
  }
`;

const StyledDetailLabel = styled.div`
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: var(--ui-text-muted, ${palette.Gray});
  margin-bottom: 4px;
`;

const StyledDetailValue = styled.div`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text, ${palette.Black});
  word-break: break-word;
`;

const StyledDetailPlaceholder = styled.div`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
`;

/** Grid cell — label above value, no list chrome (legacy participant detail grids). */
const StyledCellItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  min-width: 0;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
`;

const StyledCellLabel = styled.span`
  font-size: ${rem(12)};
  line-height: ${rem(14)};
  color: var(--ui-text-muted, ${palette.Gray});
`;

const StyledCellValue = styled.span`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text, ${palette.Black});
  word-break: break-word;
`;

const StyledCellPlaceholder = styled.span`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text, ${palette.Black});
`;

const truncateStyles = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledTruncatedValue = styled.span`
  ${truncateStyles}
  display: block;
`;

const StyledTruncatedDetailValue = styled.div`
  ${truncateStyles}
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  color: var(--ui-text, ${palette.Black});
`;

function TruncatedValueWrapper({
  children,
  display,
  Wrapper,
}: {
  children: React.ReactNode;
  display: string;
  Wrapper: React.ComponentType<{ ref?: React.Ref<HTMLElement>; children?: React.ReactNode }>;
}) {
  const ref = React.useRef<HTMLElement>(null);
  const isOverflowing = useIsOverflowing(ref, [display]);

  const content = <Wrapper ref={ref}>{children}</Wrapper>;

  if (!isOverflowing) return content;

  return (
    <Tooltip.Root side="top" align="start" variant="dark" size="md">
      <Tooltip.Trigger asChild>{content}</Tooltip.Trigger>
      <Tooltip.Content>{display}</Tooltip.Content>
    </Tooltip.Root>
  );
}

const DescriptionListItem = React.forwardRef<HTMLDivElement, DescriptionListItemProps>(
  function DescriptionListItem(
    { icon, label, value, placeholder = 'Not Specified', action, layout = 'stacked', size = 'sm', truncate = false, semanticId, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const hasValue = value != null && value !== '';
    const displayText = typeof value === 'string' ? value : '';
    const canTruncate = truncate && hasValue && typeof value === 'string';

    if (layout === 'detail') {
      const valueNode = hasValue
        ? canTruncate
          ? <TruncatedValueWrapper display={displayText} Wrapper={StyledTruncatedDetailValue}>{value}</TruncatedValueWrapper>
          : <StyledDetailValue>{value}</StyledDetailValue>
        : <StyledDetailPlaceholder>{placeholder}</StyledDetailPlaceholder>;

      return (
        <StyledDetailItem ref={ref} {...semanticProps} {...rest}>
          {icon && <StyledDetailIconSlot>{icon}</StyledDetailIconSlot>}
          <div style={{ minWidth: 0 }}>
            <StyledDetailLabel>{label}</StyledDetailLabel>
            {valueNode}
            {action && <StyledAction>{action}</StyledAction>}
          </div>
        </StyledDetailItem>
      );
    }

    if (layout === 'cell') {
      const valueNode = hasValue
        ? canTruncate
          ? <TruncatedValueWrapper display={displayText} Wrapper={StyledCellValue}>{value}</TruncatedValueWrapper>
          : <StyledCellValue>{value}</StyledCellValue>
        : <StyledCellPlaceholder>{placeholder}</StyledCellPlaceholder>;

      return (
        <StyledCellItem ref={ref} {...semanticProps} {...rest}>
          <StyledCellLabel>{label}</StyledCellLabel>
          {valueNode}
        </StyledCellItem>
      );
    }

    if (layout === 'inline') {
      const valueNode = hasValue
        ? canTruncate
          ? <TruncatedValueWrapper display={displayText} Wrapper={StyledTruncatedValue}>{value}</TruncatedValueWrapper>
          : <StyledValue $size={size}>{value}</StyledValue>
        : <StyledPlaceholder $size={size}>{placeholder}</StyledPlaceholder>;

      return (
        <StyledItem ref={ref} $layout="inline" {...semanticProps} {...rest}>
          <StyledIconSlot>
            {icon}
            <StyledLabel $size={size}>{label}</StyledLabel>
          </StyledIconSlot>
          <StyledContent>
            {valueNode}
            {action && <StyledAction>{action}</StyledAction>}
          </StyledContent>
        </StyledItem>
      );
    }

    const valueNode = hasValue
      ? canTruncate
        ? <TruncatedValueWrapper display={displayText} Wrapper={StyledTruncatedValue}>{value}</TruncatedValueWrapper>
        : <StyledValue $size={size}>{value}</StyledValue>
      : <StyledPlaceholder $size={size}>{placeholder}</StyledPlaceholder>;

    return (
      <StyledItem ref={ref} $layout="stacked" {...semanticProps} {...rest}>
        <StyledContent>
          {icon && <StyledIconSlot>{icon}</StyledIconSlot>}
          <StyledStackedLabel>{label}</StyledStackedLabel>
          {valueNode}
        </StyledContent>
        {action && <StyledAction>{action}</StyledAction>}
      </StyledItem>
    );
  },
);

DescriptionListItem.displayName = 'DescriptionList.Item';

/* ─────────────────────────── DescriptionList.Group ────────────────────── */

export interface DescriptionListGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  action?: React.ReactNode;
  loading?: boolean;
  skeletonRows?: number;
  semanticId?: string;
}

const StyledGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(18)};
`;

const StyledGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${rem(18)};

  button {
    border-color: transparent;
  }
`;

const StyledGroupTitle = styled.h5`
  margin: 0;
  font-size: ${rem(14)};
  font-weight: 500;
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
`;

const StyledSkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(14)} 0;
  border-bottom: 1px solid var(--ui-border-muted, ${palette.CulturedWhite});

  &:last-child {
    border-bottom: none;
  }
`;

const StyledSkeletonCircle = styled.div`
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: 50%;
  background: var(--ui-border-muted, ${palette.CulturedWhite});
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const StyledSkeletonLine = styled.div<{ $width: string }>`
  height: ${rem(12)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  background: var(--ui-border-muted, ${palette.CulturedWhite});
  width: ${({ $width }) => $width};
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const DescriptionListGroup = React.forwardRef<HTMLDivElement, DescriptionListGroupProps>(
  function DescriptionListGroup(
    { title, action, loading = false, skeletonRows = 5, semanticId, children, ...rest },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);

    return (
      <StyledGroup ref={ref} {...semanticProps} {...rest}>
        {(title || action) && (
          <StyledGroupHeader>
            {title && <StyledGroupTitle>{title}</StyledGroupTitle>}
            {action && <div>{action}</div>}
          </StyledGroupHeader>
        )}
        {loading
          ? Array.from({ length: skeletonRows }, (_, i) => (
              <StyledSkeletonRow key={i}>
                <StyledSkeletonCircle />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: rem(6) }}>
                  <StyledSkeletonLine $width="30%" />
                  <StyledSkeletonLine $width={`${50 + Math.random() * 30}%`} />
                </div>
              </StyledSkeletonRow>
            ))
          : children}
      </StyledGroup>
    );
  },
);

DescriptionListGroup.displayName = 'DescriptionList.Group';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const DescriptionList = Object.assign(DescriptionListRoot, {
  Item: DescriptionListItem,
  Group: DescriptionListGroup,
});
